
import {
  BookingV3,
  CustomerBookingV3,
  ErrorBookingV3,
  CommodityBookingV3,
  BookingFormType,
  FraudStatus,
  ConfigValidationForm
} from "@/domain/entities/CreateBookingV3";
import { Options, prop, Vue } from "vue-class-component";
import Checkbox from "primevue/checkbox";
import { BookingController } from "@/app/ui/controllers/BookingController";
import debounce from "lodash/debounce";
import {
  senderSchema,
  senderPhoneSchema,
  senderReceiverSchema
} from "./validation";
import {
  parseMultiFieldZodError,
  parseZodError,
  validationCurrentIndex
} from "../../utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { getPhoneDisplayCode } from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { RequestListCommodity } from "@/data/payload/api/CommodityApiRequest";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { removeNewLineWhitespace } from "@/app/infrastructures/misc/Utils";
import { CustomerListEntities, ResponsePhoneChecker } from "@/domain/entities/Booking";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import {
  SttAdjustmentRule,
  SttAdjustment
} from "@/domain/entities/ShipmentBooking";
import { OptionsClass } from "@/domain/entities/MainApp";
import { shouldSkipPhoneValidation } from "../../../booking-utils";

type CustomerParams = {
  search: string;
  isSender?: boolean;
};

class Props {
  modelValue = prop<BookingV3>({
    default: new BookingV3(),
    type: Object
  });
  errors = prop<ErrorBookingV3>({
    default: new ErrorBookingV3(),
    type: Object
  });
  formType = prop<string>({
    default: BookingFormType.MANUAL,
    type: String
  });
  currentIndex = prop<number>({
    default: -1,
    type: Number
  });
  senderNameIndex = prop<number>({
    default: 1,
    type: Number
  });
  senderPhoneIndex = prop<number>({
    default: 2,
    type: Number
  });
  senderAddressIndex = prop<number>({
    default: 3,
    type: Number
  });
  saveSenderIndex = prop<number>({
    default: 3,
    type: Number
  });
  configName = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configPhone = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configSaveAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAddress = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configRule = prop<SttAdjustment>({
    default: new SttAdjustment(),
    type: Object
  });
  isShowSaveSender = prop<boolean>({
    default: true,
    type: Boolean
  });
  isReverseJourneyDetail = prop<boolean>({
    default: false
  });
  checkFraud = prop<boolean>({
    default: true,
    type: Boolean
  });
  isPhoneCodeSet = prop<boolean>({
    default: false
  });
  countryCodeButtonId = prop<string>({
    default: "button_country_code_sender_booking",
    type: String
  });
  phoneNumberFieldId = prop<string>({
    default: "field_phone_number_booking",
    type: String
  });
}

@Options({
  components: {
    Checkbox
  },
  emits: ["update:currentIndex", "currentIndevalue"],
  watch: {
    "modelValue.phoneCodeSender": {
      handler() {
        if (this.modelValue.phoneSender) {
          this.onValidatePhone();
        }
      }
    },
    "modelValue.phoneSender": {
      handler() {
        this.onValidatePhone();
      }
    },
    "modelValue.sender": {
      handler() {
        this.onValidateName();
      }
    },
    "modelValue.addressSender": {
      handler() {
        this.onValidateAddress();
      }
    }
  }
})
export default class DetailSender extends Vue.with(Props) {
  mounted(): void {
    if (!this.isPhoneCodeSet || !this.modelValue.phoneCodeSender) {
      this.modelValue.phoneCodeSender = getPhoneDisplayCode(
        !this.accountIsForeign ? "ID" : "MY"
      );
      this.modelValue.phoneFlagSender = !this.accountIsForeign
        ? "indonesia"
        : "malaysia";
    }

    if (this.isBookingManual) {
      this.getCustomerList();
    }
  }

  get disabledCommodity() {
    return this.configRule.commodity.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get disabledName() {
    return this.configName.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledPhone() {
    return this.configPhone.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get disabledSaveAddress() {
    return this.configSaveAddress.rule.includes(
      ConfigValidationForm.NON_EDITABLE
    );
  }

  get disabledAddress() {
    return this.configAddress.rule.includes(ConfigValidationForm.NON_EDITABLE);
  }

  get accountIsForeign(): boolean {
    return AccountController.accountData.countryCode !== "id";
  }

  get isBookingClient() {
    return this.formType === BookingFormType.CLIENT;
  }

  get isBookingManual() {
    return this.formType === BookingFormType.MANUAL;
  }

  get districtDetail() {
    return LocationController.districtDetail;
  }

  // sender name
  get loadingCustomerList(): boolean {
    return BookingController.isLoadingCustomer;
  }
  get customerList() {
    if (this.isBookingClient && !this.modelValue.clientName.id || this.isReverseJourneyDetail) return [];
    return BookingController.senderCustomerList.data;
  }
  fetchCustomer = debounce((search: string) => {
    this.getCustomerList({
      search,
      isSender: true
    });
  }, 1000);
  get customerRef() {
    return {
      refType:
        !AccountController.accountData.isPosAccount &&
        this.isBookingClient &&
        this.modelValue.clientName.id
          ? "client"
          : "",
      refId: AccountController.accountData.isPosAccount
        ? 0
        : this.modelValue.clientName.id ?? 0
    };
  }
  getCustomerList(params?: CustomerParams) {
    if (this.isBookingClient && !this.modelValue.clientName.id || this.isReverseJourneyDetail ) return;
    BookingController.getCustomerList({
      ...params,
      cache: true,
      search: params?.search || "",
      refType: this.customerRef.refType,
      refId: this.customerRef.refId
    });
  }
  onChangeName(value: string) {
    const trimmed = value.trim();
    this.modelValue.sender = trimmed;
    this.onValidateName();

      if (!trimmed || !this.errorMessageName) {
      this.fetchCustomer(trimmed);
    }

  }
  onFocusCustomer(index: number, search: string) {
    this.onSelectCurrentIndex(index);

     this.getCustomerList({
      search,
      isSender: true
    });
  }
  onSelectCustomer(item: CustomerBookingV3) {
    if (!item || this.isReverseJourneyDetail) return false;

    if (item.customerCommodityCode) {
      void this.updateCommodity(item); // ✅ non-blocking, no lint warning
    } else if (!this.disabledCommodity) {
      this.modelValue.commodity = new OptionsClass();
    }

    this.modelValue.sender = item.customerName;
    this.modelValue.phoneCodeSender = item.customerPhoneCode;
    this.modelValue.phoneSender = item.customerPhoneNumber;
    this.modelValue.addressSender = item.customerAddress;
    this.modelValue.phoneFlagSender = item.customerPhoneflag;

    this.onValidateName();
    this.onValidatePhone();
    this.onValidateAddress();
  }
  async updateCommodity(item: CustomerBookingV3) {
    try {
      const commodity = await CommodityController.getDetailCommodityByCode(
        item.customerCommodityCode
      );
      await CommodityController.getCommodityList(new RequestListCommodity());

      if (!this.disabledCommodity) {
        this.modelValue.commodity = new OptionsClass({
          id: commodity.commodity_id,
          name: !this.accountIsForeign
            ? commodity.commodity_name
            : commodity.commodity_name_en || " ",
          code: commodity.commodity_code,
          isDangerousGoods: commodity.isDangerousGoods,
          isQuarantine: commodity.isQuarantine,
          status: commodity.commodity_status
        });
      }

      this.$emit("autofillCommodity");
    } catch (error) {
      this.modelValue.commodityCode = new CommodityBookingV3({
        isNotFound: true
      });
    }
  }
  get errorMessageName(): string {
    if (validationCurrentIndex(this.senderNameIndex, this.currentIndex)) {
      return this.onValidateName();
    }

    return this.errors.sender;
  }
  onValidateName() {
    const message = parseZodError(senderSchema, this.modelValue.sender);
    this.errors.sender = message;

    return message;
  }

  // sender phone number
  isTooltipLimited = false;
  toggleTooltipLimited(event: any) {
    this.isTooltipLimited = !this.isTooltipLimited;

    const refs: any = this.$refs;
    refs?.["limited"]?.toggle(event);
  }

  get errorMessagePhone(): string {
    if (validationCurrentIndex(this.senderPhoneIndex, this.currentIndex)) {
      return this.onValidatePhone();
    }

    return this.errors.phoneSender;
  }
  onValidatePhone() {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, this.disabledPhone)) {
      this.errors.phoneSender = "";
      return "";
    }

    const message = parseZodError(senderPhoneSchema, this.fullPhoneNumber);
    this.errors.phoneSender = message;

    return message;
  }
  get fullPhoneNumber() {
    const { phoneCodeSender, phoneSender } = this.modelValue;
    return phoneSender
      ? `${phoneCodeSender}${phoneSender}`.replace("+", "")
      : phoneSender;
  }
  onChangePhone() {
    const { phoneCodeSender, phoneSender } = this.modelValue;

    this.modelValue.phoneCodeSender = phoneCodeSender;
    this.modelValue.phoneSender = phoneSender;
    this.onValidatePhone()

    if (!this.errorMessagePhone) {
      this.fetchCustomer(this.fullPhoneNumber);
      this.onCheckFraudNumber(this.fullPhoneNumber);
    }
  }

  onCheckFraudNumber = debounce(async (phone: string) => {
    if (shouldSkipPhoneValidation(this.fullPhoneNumber, this.disabledPhone)) {
      return;
    }

    BookingController.setPhoneCheckerData(new ResponsePhoneChecker());

    const invalidPhone = this.errors.phoneSender || this.isBookingClient;

    if (invalidPhone || !this.checkFraud) return;

    // Hit API fraud number
    await BookingController.phoneChecker(phone);

    const isBlacklist = BookingController.phoneCheckerData.isBlacklist;
    const errorFraudLimited =
      BookingController.phoneCheckerData.status === FraudStatus.LIMITED &&
      FlagsPermissionBooking.disable_cod_dfod_for_limited_number.isEnabled();

    if (isBlacklist || errorFraudLimited) {
      this.modelValue.isCOD = false;
      this.modelValue.isDFOD = false;
    }
  }, 1000);
  get isErrorFraudLimited() {
    return BookingController.phoneCheckerData.status === FraudStatus.LIMITED;
  }
  get isErrorFraudSuspected() {
    return BookingController.phoneCheckerData.status === FraudStatus.SUSPECTED;
  }

  // sender address
  get errorMessageAddress(): string {
    if (validationCurrentIndex(this.senderAddressIndex, this.currentIndex)) {
      return this.onValidateAddress();
    }

    return this.errors.addressSender;
  }
  onValidateAddress() {
    const message = parseMultiFieldZodError(
      senderReceiverSchema,
      {
        addressReceiver: this.modelValue.addressReceiver,
        addressSender: this.modelValue.addressSender
      },
      "addressSender"
    );
    this.errors.addressSender = message;

    return message;
  }
  get maxAddressLength() {
    return FlagsMedusa.config_max_address.getValue();
  }
  replaceNewLine(value: string) {
    const removedNewLineAddress = removeNewLineWhitespace(value, " ");

    this.modelValue.addressSender = removedNewLineAddress;

    return removedNewLineAddress;
  }

  onSelectCurrentIndex(index: number) {
    setTimeout(() => {
      if (!validationCurrentIndex(index, this.currentIndex)) {
        this.$emit("update:currentIndex", index);
      }
    }, 0);
  }
  async onScroll(search: string) {
    const nextPage = BookingController.senderCustomerList.pagination.page + 1;
    const data = await BookingController.getCustomerInfiniteScrollList({
      cache: true,
      search: search || "",
      refType: this.customerRef.refType,
      refId: this.customerRef.refId,
      isSender: false,
      page: nextPage
    });
    

    BookingController.setSenderCustomerList(new CustomerListEntities(
      data.pagination,
      [...this.customerList, ...data.data]
    ));
  }
}
